import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadsApi } from '../../services/api';
import { queryKeys } from '../../lib/queryClient';
import { useToast } from '../../components/ui';
import { extractApiErrorMessage } from '../../utils/apiError';
import { ObservationMedia } from '../../types/api';

/**
 * Hook to upload media for an observation
 */
export const useUploadObservationMedia = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: ({ observationId, file, caption }: { 
      observationId: string; 
      file: File; 
      caption?: string; 
    }) => uploadsApi.uploadObservationMedia(observationId, file, caption),
    onSuccess: (data, variables) => {
      // Invalidate observation media list
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.observationMedia(variables.observationId) 
      });
      
      // Invalidate observation details to update media count
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.observation(variables.observationId) 
      });
      
      // Show success message
      addToast({
        title: 'Media uploaded',
        message: `${data.file_name} has been uploaded successfully`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to upload media',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to delete observation media
 */
export const useDeleteObservationMedia = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (mediaId: string) => uploadsApi.deleteObservationMedia(mediaId),
    onSuccess: (_, mediaId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.observationMediaItem(mediaId) });
      
      // Invalidate observation media lists
      queryClient.invalidateQueries({ queryKey: queryKeys.observationMedia() });
      
      // Show success message
      addToast({
        title: 'Media deleted',
        message: 'The media file has been removed successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to delete media',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to get observation media details
 */
export const useObservationMedia = (mediaId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.observationMediaItem(mediaId),
    queryFn: () => uploadsApi.getObservationMedia(mediaId),
    enabled: enabled && !!mediaId,
  });
};

/**
 * Hook to update observation media caption
 */
export const useUpdateObservationMedia = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: ({ mediaId, caption }: { mediaId: string; caption: string }) => 
      uploadsApi.updateObservationMedia(mediaId, caption),
    onSuccess: (data, variables) => {
      // Update cache for specific media item
      queryClient.setQueryData(queryKeys.observationMediaItem(variables.mediaId), data);
      
      // Invalidate observation media lists
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.observationMedia(data.observation_id) 
      });
      
      // Show success message
      addToast({
        title: 'Caption updated',
        message: 'The media caption has been updated successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to update caption',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to get all media for an observation
 */
export const useObservationMediaList = (observationId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.observationMedia(observationId),
    queryFn: () => uploadsApi.getObservationMediaList(observationId),
    enabled: enabled && !!observationId,
  });
};

/**
 * Hook to upload profile photo
 */
export const useUploadProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (file: File) => uploadsApi.uploadProfilePhoto(file),
    onSuccess: (_data) => {
      // Invalidate current user data
      queryClient.invalidateQueries({ queryKey: queryKeys.userMe() });
      
      // Show success message
      addToast({
        title: 'Profile photo updated',
        message: 'Your profile photo has been updated successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to update profile photo',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to delete profile photo
 */
export const useDeleteProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: () => uploadsApi.deleteProfilePhoto(),
    onSuccess: () => {
      // Invalidate current user data
      queryClient.invalidateQueries({ queryKey: queryKeys.userMe() });
      
      // Show success message
      addToast({
        title: 'Profile photo deleted',
        message: 'Your profile photo has been removed successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to delete profile photo',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to validate files before upload
 */
export const useFileValidation = () => {
  const { addToast } = useToast();
  
  return {
    validateObservationMedia: (file: File) => {
      const validation = uploadsApi.validateObservationMedia(file);
      
      if (!validation.isValid) {
        addToast({
          title: 'Invalid file',
          message: validation.error || 'File is not valid',
          type: 'error',
        });
      }
      
      return validation;
    },
    
    validateProfilePhoto: (file: File) => {
      const validation = uploadsApi.validateProfilePhoto(file);
      
      if (!validation.isValid) {
        addToast({
          title: 'Invalid image',
          message: validation.error || 'Image is not valid',
          type: 'error',
        });
      }
      
      return validation;
    },
    
    getMediaType: uploadsApi.getMediaType,
    formatFileSize: uploadsApi.formatFileSize,
  };
};

/**
 * Hook to handle multiple file uploads with progress
 */
export const useMultipleFileUpload = () => {
  const uploadMutation = useUploadObservationMedia();
  const { addToast } = useToast();
  
  const uploadFiles = async (
    observationId: string,
    files: File[],
    onProgress?: (progress: { completed: number; total: number }) => void
  ) => {
    const results: (ObservationMedia | Error)[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const result = await uploadMutation.mutateAsync({
          observationId,
          file,
          caption: file.name,
        });
        
        results.push(result);
        
        if (onProgress) {
          onProgress({ completed: i + 1, total: files.length });
        }
      } catch (error) {
        results.push(error as Error);
        
        addToast({
          title: `Failed to upload ${file.name}`,
          message: 'This file was skipped due to an error',
          type: 'error',
        });
      }
    }
    
    const successful = results.filter(r => !(r instanceof Error));
    const failed = results.filter(r => r instanceof Error);
    
    if (successful.length > 0) {
      addToast({
        title: 'Upload complete',
        message: `${successful.length} file(s) uploaded successfully${
          failed.length > 0 ? `, ${failed.length} failed` : ''
        }`,
        type: successful.length === files.length ? 'success' : 'warning',
      });
    }
    
    return { successful, failed };
  };
  
  return {
    uploadFiles,
    isUploading: uploadMutation.isPending,
  };
};
